import type { Board, GameState, Move, MoveRecord, Outcome, Piece, PieceType, Side } from './types';

export const ROWS = 10;
export const COLS = 9;
export const INITIAL_FEN = 'rheakaehr/9/1c5c1/p1p1p1p1p/9/9/P1P1P1P1P/1C5C1/9/RHEAKAEHR';

const PIECE_LABELS: Record<Side, Record<PieceType, string>> = {
  red: { K: '帥', A: '仕', E: '相', H: '馬', R: '車', C: '炮', P: '兵' },
  black: { K: '將', A: '士', E: '象', H: '馬', R: '車', C: '砲', P: '卒' },
};

export const opposite = (side: Side): Side => (side === 'red' ? 'black' : 'red');
export const indexOf = (row: number, col: number): number => row * COLS + col;
export const rowOf = (index: number): number => Math.floor(index / COLS);
export const colOf = (index: number): number => index % COLS;
export const inBounds = (row: number, col: number): boolean => row >= 0 && row < ROWS && col >= 0 && col < COLS;

export function pieceLabel(piece: Piece): string {
  return PIECE_LABELS[piece.side][piece.type];
}

export function boardFromFen(fen: string): Board {
  const rows = fen.split('/');
  if (rows.length !== ROWS) throw new Error('棋盤格式錯誤：必須有十列');
  const board: Board = Array.from({ length: ROWS * COLS }, () => null);
  const valid = new Set(['k', 'a', 'e', 'h', 'r', 'c', 'p']);
  rows.forEach((rank, row) => {
    let col = 0;
    for (const char of rank) {
      if (/\d/.test(char)) {
        col += Number(char);
      } else {
        const lower = char.toLowerCase();
        if (!valid.has(lower) || col >= COLS) throw new Error('棋盤格式含無效棋子');
        board[indexOf(row, col)] = {
          side: char === char.toUpperCase() ? 'red' : 'black',
          type: lower.toUpperCase() as PieceType,
        };
        col += 1;
      }
    }
    if (col !== COLS) throw new Error(`棋盤第 ${row + 1} 列長度錯誤`);
  });
  return board;
}

export function boardToFen(board: Board): string {
  const ranks: string[] = [];
  for (let row = 0; row < ROWS; row += 1) {
    let rank = '';
    let empty = 0;
    for (let col = 0; col < COLS; col += 1) {
      const piece = board[indexOf(row, col)];
      if (!piece) {
        empty += 1;
        continue;
      }
      if (empty) {
        rank += String(empty);
        empty = 0;
      }
      const code = piece.type.toLowerCase();
      rank += piece.side === 'red' ? code.toUpperCase() : code;
    }
    if (empty) rank += String(empty);
    ranks.push(rank);
  }
  return ranks.join('/');
}

export function positionKey(board: Board, sideToMove: Side): string {
  return `${boardToFen(board)} ${sideToMove}`;
}

function canLand(board: Board, to: number, side: Side): boolean {
  return !board[to] || board[to]?.side !== side;
}

function addIfValid(board: Board, moves: Move[], from: number, row: number, col: number, side: Side): void {
  if (!inBounds(row, col)) return;
  const to = indexOf(row, col);
  if (canLand(board, to, side)) moves.push({ from, to });
}

export function generatePseudoMoves(board: Board, from: number): Move[] {
  const piece = board[from];
  if (!piece) return [];
  const moves: Move[] = [];
  const row = rowOf(from);
  const col = colOf(from);

  if (piece.type === 'K') {
    const palaceRows = piece.side === 'red' ? [7, 8, 9] : [0, 1, 2];
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
      const nr = row + dr;
      const nc = col + dc;
      if (palaceRows.includes(nr) && nc >= 3 && nc <= 5) addIfValid(board, moves, from, nr, nc, piece.side);
    }
    for (const direction of [-1, 1]) {
      for (let nr = row + direction; nr >= 0 && nr < ROWS; nr += direction) {
        const target = board[indexOf(nr, col)];
        if (!target) continue;
        if (target.side !== piece.side && target.type === 'K') moves.push({ from, to: indexOf(nr, col) });
        break;
      }
    }
  }

  if (piece.type === 'A') {
    const palaceRows = piece.side === 'red' ? [7, 8, 9] : [0, 1, 2];
    for (const [dr, dc] of [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const) {
      const nr = row + dr;
      const nc = col + dc;
      if (palaceRows.includes(nr) && nc >= 3 && nc <= 5) addIfValid(board, moves, from, nr, nc, piece.side);
    }
  }

  if (piece.type === 'E') {
    for (const [dr, dc] of [[-2, -2], [-2, 2], [2, -2], [2, 2]] as const) {
      const nr = row + dr;
      const nc = col + dc;
      const eye = indexOf(row + dr / 2, col + dc / 2);
      const staysHome = piece.side === 'red' ? nr >= 5 : nr <= 4;
      if (inBounds(nr, nc) && staysHome && !board[eye]) addIfValid(board, moves, from, nr, nc, piece.side);
    }
  }

  if (piece.type === 'H') {
    const horseSteps = [
      [-2, -1, -1, 0], [-2, 1, -1, 0], [2, -1, 1, 0], [2, 1, 1, 0],
      [-1, -2, 0, -1], [1, -2, 0, -1], [-1, 2, 0, 1], [1, 2, 0, 1],
    ] as const;
    for (const [dr, dc, lr, lc] of horseSteps) {
      const legRow = row + lr;
      const legCol = col + lc;
      if (inBounds(legRow, legCol) && !board[indexOf(legRow, legCol)]) {
        addIfValid(board, moves, from, row + dr, col + dc, piece.side);
      }
    }
  }

  if (piece.type === 'R' || piece.type === 'C') {
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
      let screenFound = false;
      for (let nr = row + dr, nc = col + dc; inBounds(nr, nc); nr += dr, nc += dc) {
        const to = indexOf(nr, nc);
        const target = board[to];
        if (piece.type === 'R') {
          if (!target) moves.push({ from, to });
          else {
            if (target.side !== piece.side) moves.push({ from, to });
            break;
          }
          continue;
        }
        if (!screenFound) {
          if (!target) moves.push({ from, to });
          else screenFound = true;
        } else if (target) {
          if (target.side !== piece.side) moves.push({ from, to });
          break;
        }
      }
    }
  }

  if (piece.type === 'P') {
    const forward = piece.side === 'red' ? -1 : 1;
    addIfValid(board, moves, from, row + forward, col, piece.side);
    const crossed = piece.side === 'red' ? row <= 4 : row >= 5;
    if (crossed) {
      addIfValid(board, moves, from, row, col - 1, piece.side);
      addIfValid(board, moves, from, row, col + 1, piece.side);
    }
  }

  return moves;
}

export function applyMoveToBoard(board: Board, move: Move): Board {
  const next = board.slice();
  next[move.to] = next[move.from] ?? null;
  next[move.from] = null;
  return next;
}

export function findGeneral(board: Board, side: Side): number {
  return board.findIndex((piece) => piece?.side === side && piece.type === 'K');
}

export function isInCheck(board: Board, side: Side): boolean {
  const general = findGeneral(board, side);
  if (general < 0) return true;
  const enemy = opposite(side);
  for (let from = 0; from < board.length; from += 1) {
    if (board[from]?.side !== enemy) continue;
    if (generatePseudoMoves(board, from).some((move) => move.to === general)) return true;
  }
  return false;
}

export function generateLegalMoves(board: Board, side: Side): Move[] {
  const moves: Move[] = [];
  for (let from = 0; from < board.length; from += 1) {
    if (board[from]?.side !== side) continue;
    for (const move of generatePseudoMoves(board, from)) {
      if (!isInCheck(applyMoveToBoard(board, move), side)) moves.push(move);
    }
  }
  return moves;
}

export function isLegalMove(board: Board, side: Side, move: Move): boolean {
  return generateLegalMoves(board, side).some((candidate) => candidate.from === move.from && candidate.to === move.to);
}

export function hasInsufficientMaterial(board: Board): boolean {
  return board.every((piece) => !piece || ['K', 'A', 'E'].includes(piece.type));
}

function outcomeAfterMove(state: GameState, board: Board, nextSide: Side, counts: Record<string, number>, noCapturePly: number): { outcome: Outcome; reason: string } {
  if (findGeneral(board, nextSide) < 0) {
    return { outcome: nextSide === 'red' ? 'black_win' : 'red_win', reason: '將帥被擒' };
  }
  const key = positionKey(board, nextSide);
  if ((counts[key] ?? 0) >= 3) return { outcome: 'draw', reason: '同一局面重複三次' };
  if (noCapturePly >= 120) return { outcome: 'draw', reason: '六十回合未吃子' };
  if (hasInsufficientMaterial(board)) return { outcome: 'draw', reason: '雙方子力不足' };
  if (generateLegalMoves(board, nextSide).length === 0) {
    return {
      outcome: nextSide === 'red' ? 'black_win' : 'red_win',
      reason: isInCheck(board, nextSide) ? '將死' : '困斃',
    };
  }
  return { outcome: 'ongoing', reason: '' };
}

function fileName(index: number, side: Side): string {
  const chinese = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const file = side === 'red' ? 8 - colOf(index) : colOf(index);
  return chinese[file] ?? String(file + 1);
}

function sameFilePrefix(board: Board | undefined, piece: Piece, from: number): string | null {
  if (!board) return null;
  const matches = board
    .map((candidate, index) => ({ candidate, index }))
    .filter(({ candidate, index }) => candidate?.side === piece.side && candidate.type === piece.type && colOf(index) === colOf(from))
    .sort((a, b) => piece.side === 'red' ? rowOf(a.index) - rowOf(b.index) : rowOf(b.index) - rowOf(a.index));
  if (matches.length < 2) return null;
  const position = matches.findIndex(({ index }) => index === from);
  if (position === 0) return '前';
  if (position === matches.length - 1) return '後';
  if (matches.length === 3) return '中';
  return ['一', '二', '三', '四', '五'][position] ?? String(position + 1);
}

export function moveNotation(piece: Piece, from: number, to: number, board?: Board): string {
  const fromRow = rowOf(from);
  const toRow = rowOf(to);
  const prefix = sameFilePrefix(board, piece, from);
  const head = prefix ? `${prefix}${pieceLabel(piece)}` : `${pieceLabel(piece)}${fileName(from, piece.side)}`;
  if (fromRow === toRow) return `${head}平${fileName(to, piece.side)}`;
  const advancing = piece.side === 'red' ? toRow < fromRow : toRow > fromRow;
  const action = advancing ? '進' : '退';
  const diagonalPiece = piece.type === 'H' || piece.type === 'E' || piece.type === 'A';
  const distance = ['一', '二', '三', '四', '五', '六', '七', '八', '九'][Math.abs(toRow - fromRow) - 1] ?? String(Math.abs(toRow - fromRow));
  return `${head}${action}${diagonalPiece ? fileName(to, piece.side) : distance}`;
}

export function playMove(state: GameState, move: Move): GameState {
  if (state.outcome !== 'ongoing') throw new Error('棋局已結束');
  if (!isLegalMove(state.board, state.sideToMove, move)) throw new Error('不合法的走法');
  const piece = state.board[move.from];
  if (!piece) throw new Error('起點沒有棋子');
  const captured = state.board[move.to];
  const board = applyMoveToBoard(state.board, move);
  const nextSide = opposite(state.sideToMove);
  const key = positionKey(board, nextSide);
  const counts = { ...state.positionCounts, [key]: (state.positionCounts[key] ?? 0) + 1 };
  const noCapturePly = captured ? 0 : state.noCapturePly + 1;
  const result = outcomeAfterMove(state, board, nextSide, counts, noCapturePly);
  const record: MoveRecord = {
    ...move,
    piece: { ...piece },
    captured: captured ? { ...captured } : null,
    notation: moveNotation(piece, move.from, move.to, state.board),
  };
  return {
    ...state,
    board,
    sideToMove: nextSide,
    moves: [...state.moves, record],
    positionCounts: counts,
    noCapturePly,
    outcome: result.outcome,
    outcomeReason: result.reason,
  };
}

export function createInitialGame(seed = Date.now() >>> 0): GameState {
  const board = boardFromFen(INITIAL_FEN);
  return {
    gameId: globalThis.crypto?.randomUUID?.() ?? `game-${seed}-${Date.now()}`,
    board,
    sideToMove: 'red',
    moves: [],
    positionCounts: { [positionKey(board, 'red')]: 1 },
    noCapturePly: 0,
    outcome: 'ongoing',
    outcomeReason: '',
    seed,
  };
}

export function declareOutcome(state: GameState, outcome: Outcome, reason: string): GameState {
  return { ...state, outcome, outcomeReason: reason };
}
